import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { EmerchConsentRequest } from '@/types/emerch-consent';

interface EmerchConsentDialogProps {
  request: EmerchConsentRequest | null;
  onClose: () => void;
}

export function EmerchConsentDialog({
  request,
  onClose,
}: EmerchConsentDialogProps) {
  const { t } = useTranslation();
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const decisionMadeRef = useRef(false);

  const isOpen = request !== null;
  const hasSelectedColumns = selectedColumns.length > 0;

  useEffect(() => {
    setSelectedColumns(request?.columns ?? []);
    decisionMadeRef.current = false;
  }, [request]);

  const handleToggleColumn = (column: string, checked: boolean) => {
    setSelectedColumns((currentColumns) => {
      if (checked) {
        return currentColumns.includes(column)
          ? currentColumns
          : [...currentColumns, column];
      }

      return currentColumns.filter((currentColumn) => currentColumn !== column);
    });
  };

  const handleShare = () => {
    if (!request || !hasSelectedColumns) {
      return;
    }

    decisionMadeRef.current = true;
    request.onDecision(selectedColumns);
    onClose();
  };

  const handleIgnore = () => {
    if (!decisionMadeRef.current) {
      decisionMadeRef.current = true;
      request?.onDecision(null);
    }
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleIgnore();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('components.import_flow.emerch_consent.title')}
          </DialogTitle>
          <DialogDescription>
            {t('components.import_flow.emerch_consent.subtitle')}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-80">
          <div className="space-y-3 pr-4">
            {request?.columns.map((column) => {
              const checkboxId = `emerch-column-${column}`;

              return (
                <Label
                  key={column}
                  htmlFor={checkboxId}
                  className="flex cursor-pointer items-start gap-3 rounded-md border px-3 py-3"
                >
                  <Checkbox
                    id={checkboxId}
                    checked={selectedColumns.includes(column)}
                    onCheckedChange={(checked) =>
                      handleToggleColumn(column, checked === true)
                    }
                    className="mt-0.5"
                  />
                  <span className="text-sm font-medium leading-5">
                    {column}
                  </span>
                </Label>
              );
            })}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={handleIgnore}>
            {t('components.import_flow.emerch_consent.ignore')}
          </Button>
          <Button onClick={handleShare} disabled={!hasSelectedColumns}>
            {t('components.import_flow.emerch_consent.share')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
